import _extends from "@babel/runtime/helpers/extends";
import TextField from "@material-ui/core/esm/TextField";
import { KeyboardDatePicker } from '@material-ui/pickers';
import dayjs from 'dayjs';
import { MuiTelInput } from 'mui-tel-input';
import { nanoid } from 'nanoid';
import React, { useEffect, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormElement } from 'components/Form';
import { Header } from 'components/Text';
import { NORWEGIAN_DATE_FORMAT, UNIVERSAL_DATE_FORMAT } from 'constants/dateFormats';
import { DEFAULT_LANGUAGE } from 'constants/language';
import { birthDateEN, birthDateNO } from 'constants/regex';
import useGetValidationString from 'hooks/language/useGetValidationString';
import showPhoneNumberError from 'utils/form/showPhoneNumberError';
import isNorwegian from 'utils/language/isNorwegian';
import getPhoneNumberError from 'utils/phone/getPhoneNumberError';
import isValidPhoneNumber from 'utils/phone/isValidPhoneNumber';
import { isValidDate, isValidAge } from 'utils/text/isDateValid';
import isValidEmail, { normalizeEmail } from 'utils/text/isValidEmail';
import useStyles from '../CreateCustomerStyles';
import { getUserDataFromSession } from 'utils/account/getUserDataFromSession';
const idSuffix = nanoid();
export default function CreateCustomerPersonalia() {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    textField
  } = useStyles();
  const [readonlyNameAndBirthInfo, setReadonlyNameAndBirthInfo] = useState(false);
  const [readonlyBirthDate, setReadonlyBirthDate] = useState(false);
  const {
    watch,
    register,
    getValues,
    errors,
    control,
    setValue,
    trigger
  } = useFormContext();
  const {
    emailFormat,
    confirmEmailFormat,
    required,
    dateFormatAlt,
    ageLimit
  } = useGetValidationString();
  const isNorwegianLangSet = isNorwegian(i18n);
  const [selectedDate, setSelectedDate] = useState(null);
  const handleDateChange = value => {
    setSelectedDate(value);
  };

  // Age & DOB verification
  const getDateFormat = () => isNorwegianLangSet ? NORWEGIAN_DATE_FORMAT : UNIVERSAL_DATE_FORMAT;
  const date = getValues('personNumber');
  const dateErrorMessage = () => {
    if (!isValidDate(date, isNorwegianLangSet)) return dateFormatAlt;
    if (!isValidAge(date, isNorwegianLangSet)) return ageLimit;
  };
  const dateError = !isValidAge(date, isNorwegianLangSet) || !isValidDate(date, isNorwegianLangSet);

  // Email validation
  const emailValue = watch('email');
  const confirmEmailValue = watch('confirmEmail');
  const emailError = !!emailValue && !isValidEmail(emailValue);
  const confirmEmailError = !!confirmEmailValue && (!isValidEmail(confirmEmailValue) || normalizeEmail(confirmEmailValue) !== normalizeEmail(emailValue));

  // Re-validate the confirmation field whenever the primary email changes so
  // the form's validity (and the Create button) reflects a mismatch.
  useEffect(() => {
    if (confirmEmailValue) trigger('confirmEmail');
    // Intentionally only depends on the primary email value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue]);

  // Phone number validation
  const phoneNumberInput = watch('phoneNumber');
  const showPhoneError = showPhoneNumberError(phoneNumberInput);
  const prefillPersonFormDataFromSession = () => {
    const userData = getUserDataFromSession();
    if (!userData) return;
    setValue('personFirstName', userData.firstName, {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue('personLastName', userData.lastName, {
      shouldValidate: true,
      shouldDirty: true
    });
    const dayjsDate = dayjs(userData?.birthDate);
    if (dayjsDate.isValid()) {
      const formatted = dayjsDate.format(getDateFormat());
      setSelectedDate(dayjsDate);
      setValue('personNumber', formatted, {
        shouldValidate: true,
        shouldDirty: true
      });
      setReadonlyBirthDate(true);
    }
    setReadonlyNameAndBirthInfo(true);
  };
  useEffect(() => {
    prefillPersonFormDataFromSession();
  }, []);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Header, null, t('profile.personal_info')), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.first_name'),
    childId: `personFirstName_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    inputRef: register({
      required: true
    }),
    name: "personFirstName",
    variant: "outlined",
    placeholder: t('profile.first_name'),
    error: !!errors.personFirstName,
    helperText: errors.personFirstName ? required : null,
    inputProps: {
      id: `personFirstName_${idSuffix}`,
      readOnly: readonlyNameAndBirthInfo
    }
  })), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.last_name'),
    childId: `personLastName_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    inputRef: register({
      required: true
    }),
    name: "personLastName",
    variant: "outlined",
    placeholder: t('profile.last_name'),
    error: !!errors.personLastName,
    helperText: errors.personLastName ? required : null,
    inputProps: {
      id: `personLastName_${idSuffix}`,
      readOnly: readonlyNameAndBirthInfo
    }
  })), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.birth_date'),
    childId: `personNumber_${idSuffix}`
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "personNumber",
    rules: {
      required: true,
      pattern: isNorwegianLangSet ? birthDateNO : birthDateEN,
      validate: {
        format: date => !!isValidDate(date, isNorwegianLangSet),
        age: date => !!isValidAge(date, isNorwegianLangSet)
      }
    },
    render: ({
      onChange,
      name,
      ref
    }) => /*#__PURE__*/React.createElement(KeyboardDatePicker, {
      name: name,
      margin: "none",
      format: getDateFormat(),
      value: selectedDate,
      onChange: (date, val) => {
        handleDateChange(date);
        const dayjsDate = date;
        if (dayjsDate?.isValid()) {
          onChange(dayjsDate.format(getDateFormat()));
        } else {
          onChange(val);
        }
      },
      disabled: readonlyBirthDate,
      onAccept: date => handleDateChange(date),
      KeyboardButtonProps: {
        'aria-label': 'change birth date'
      },
      maxDate: dayjs(),
      inputVariant: "outlined",
      inputRef: ref,
      placeholder: t('placeholders.birth_date'),
      error: dateError,
      helperText: dateError ? dateErrorMessage() : null,
      InputProps: {
        inputProps: {
          id: `personNumber_${idSuffix}`,
          readOnly: readonlyBirthDate
        }
      }
    })
  })), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.telephone'),
    childId: `phoneNumber_${idSuffix}`
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "phoneNumber",
    control: control,
    rules: {
      validate: isValidPhoneNumber
    },
    render: field => /*#__PURE__*/React.createElement(MuiTelInput, _extends({}, field, {
      onChange: (_, info) => field.onChange(info.numberValue),
      variant: "outlined",
      defaultCountry: DEFAULT_LANGUAGE,
      langOfCountryName: isNorwegianLangSet ? 'NO' : 'EN',
      fullWidth: true,
      forceCallingCode: true,
      inputProps: {
        id: `phoneNumber_${idSuffix}`,
        maxLength: 17
      },
      placeholder: t('profile.telephone'),
      error: showPhoneError,
      helperText: showPhoneError ? getPhoneNumberError(phoneNumberInput, t) : t('profile.phone_number_info')
    }))
  })), /*#__PURE__*/React.createElement("div", {
    className: textField
  }, /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.email'),
    childId: `email_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    inputRef: register({
      required: true,
      validate: value => isValidEmail(value)
    }),
    name: "email",
    variant: "outlined",
    placeholder: t('profile.add_email'),
    error: emailError,
    helperText: emailError ? emailFormat : '',
    inputProps: {
      id: `email_${idSuffix}`
    }
  })), /*#__PURE__*/React.createElement(FormElement, {
    title: t('profile.confirm_email'),
    childId: `confirmEmail_${idSuffix}`
  }, /*#__PURE__*/React.createElement(TextField, {
    name: "confirmEmail",
    className: textField,
    inputRef: register({
      required: true,
      validate: value => isValidEmail(value) && normalizeEmail(value) === normalizeEmail(getValues('email'))
    }),
    variant: "outlined",
    fullWidth: true,
    error: confirmEmailError,
    helperText: confirmEmailError ? confirmEmailFormat : '',
    placeholder: t('profile.confirm_email'),
    inputProps: {
      id: `confirmEmail_${idSuffix}`
    }
  }))));
}